import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Avatar,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import MainLayout from '../layout/MainLayout';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';
import { Link } from 'react-router-dom';

// Styled components
const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  padding: '10px 20px',
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  switch (status) {
    case 'approved':
      color = theme.palette.success.main;
      break;
    case 'pending':
      color = theme.palette.warning.main;
      break;
    case 'rejected':
      color = theme.palette.error.main;
      break;
    default:
      color = theme.palette.info.main;
  }
  
  return {
    backgroundColor: `${color}10`,
    color: color,
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: color,
    },
  };
});

// API-Basis-URL
const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api';
console.log('Dashboard - API-Basis-URL:', API_BASE_URL);

const Dashboard = () => {
  const [recentRequests, setRecentRequests] = useState([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    approvedRequests: 0,
    pendingRequests: 0,
    rejectedRequests: 0,
    connectedHospitals: 0,
    completedAnalyses: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Daten vom Backend laden
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [requestsResponse, statsResponse, recentResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/requests`),
          axios.get(`${API_BASE_URL}/stats`),
          axios.get(`${API_BASE_URL}/recent-requests`)
        ]);
        
        // Verarbeiten der Statistikdaten
        const statsData = statsResponse.data;
        setStats({
          totalRequests: statsData.totalRequests || 0,
          approvedRequests: statsData.approvedRequests || 0,
          pendingRequests: statsData.processingRequests + statsData.inReviewRequests || 0,
          rejectedRequests: statsData.rejectedRequests || 0,
          connectedHospitals: new Set(requestsResponse.data.map(req => req.hospital)).size,
          completedAnalyses: statsData.completedRequests || 0
        });
        
        // Verarbeiten der aktuellen Anfragen
        setRecentRequests(recentResponse.data || []);
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError('Die Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'rejected':
        return <ErrorIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      case 'processing':
        return <BarChartIcon />;
      case 'submitted':
        return <AssignmentIcon />;
      case 'in_review':
        return <PendingIcon />;
      default:
        return <AssignmentIcon />;
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Genehmigt';
      case 'pending':
        return 'In Bearbeitung';
      case 'rejected':
        return 'Abgelehnt';
      case 'completed':
        return 'Abgeschlossen';
      case 'processing':
        return 'In Bearbeitung';
      case 'submitted':
        return 'Eingereicht';
      case 'in_review':
        return 'In Prüfung';
      default:
        return status;
    }
  };

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          Willkommen bei ARGUS Insight
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Ihr zentrales Portal für sichere und effiziente Gesundheitsdatenanalysen
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <ActionButton 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              component={Link}
              to="/request"
              fullWidth
              sx={{ height: '100%', py: 1.5 }}
            >
              Neue Datenanfrage stellen
            </ActionButton>
          </Grid>
          <Grid item xs={12} md={6}>
            <ActionButton 
              variant="outlined" 
              color="primary"
              component={Link}
              to="/my-requests"
              fullWidth
              sx={{ height: '100%', py: 1.5 }}
            >
              Meine Anfragen verwalten
            </ActionButton>
          </Grid>
        </Grid>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Statistik-Karten */}
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard className="modern-card">
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                        {stats.totalRequests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Gesamtanfragen
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56 }}>
                      <AssignmentIcon sx={{ color: 'white', fontSize: 32 }} />
                    </Avatar>
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={stats.totalRequests > 0 ? (stats.approvedRequests / stats.totalRequests) * 100 : 0} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: 'rgba(37, 99, 235, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'primary.main',
                        }
                      }} 
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {stats.approvedRequests} von {stats.totalRequests} genehmigt
                    </Typography>
                  </Box>
                </CardContent>
              </StatsCard>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard className="modern-card">
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: 'secondary.main', mb: 1 }}>
                        {stats.connectedHospitals}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Partnerkliniken
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'secondary.light', width: 56, height: 56 }}>
                      <LocalHospitalIcon sx={{ color: 'white', fontSize: 32 }} />
                    </Avatar>
                  </Box>
                  
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label="Universitätskliniken" 
                      size="small" 
                      sx={{ 
                        bgcolor: 'rgba(16, 185, 129, 0.1)', 
                        color: 'secondary.main',
                        fontWeight: 600
                      }} 
                    />
                    <Chip 
                      label="Forschungszentren" 
                      size="small" 
                      sx={{ 
                        bgcolor: 'rgba(16, 185, 129, 0.1)', 
                        color: 'secondary.main',
                        fontWeight: 600
                      }} 
                    />
                  </Box>
                </CardContent>
              </StatsCard>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard className="modern-card">
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: 'info.main', mb: 1 }}>
                        {stats.completedAnalyses}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Abgeschlossene Analysen
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'info.light', width: 56, height: 56 }}>
                      <BarChartIcon sx={{ color: 'white', fontSize: 32 }} />
                    </Avatar>
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Letzte Analyse: <strong>{recentRequests.length > 0 ? new Date(recentRequests[0].date).toLocaleDateString('de-DE') : 'Keine'}</strong>
                    </Typography>
                  </Box>
                </CardContent>
              </StatsCard>
            </Grid>
          </Grid>
        )}
      </Box>
      
      {!loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, height: '100%' }} className="modern-card">
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon color="primary" /> Aktuelle Anfragen
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              {recentRequests.length > 0 ? (
                <List sx={{ width: '100%' }}>
                  {recentRequests.map((request, index) => (
                    <React.Fragment key={request.id}>
                      <ListItem 
                        alignItems="flex-start"
                        sx={{ 
                          px: 2, 
                          py: 1.5, 
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                          mb: 1
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getStatusIcon(request.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {request.name}
                              </Typography>
                              <StatusChip 
                                label={getStatusText(request.status)} 
                                size="small" 
                                status={request.status}
                                icon={getStatusIcon(request.status)}
                              />
                            </Box>
                          }
                          secondary={
                            <React.Fragment>
                              <Typography variant="body2" color="text.secondary" component="span">
                                {request.hospital} • Erstellt am {new Date(request.date).toLocaleDateString('de-DE')}
                              </Typography>
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      {index !== recentRequests.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Keine aktuellen Anfragen vorhanden
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  component={Link}
                  to="/my-requests"
                  color="primary" 
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Alle Anfragen anzeigen
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }} className="modern-card">
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="primary" /> Partnerkliniken
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              {recentRequests.length > 0 ? (
                <List dense>
                  {Array.from(new Set(recentRequests.map(req => req.hospital)))
                    .slice(0, 3)
                    .map((hospital, index) => {
                      const requestCount = recentRequests.filter(req => req.hospital === hospital).length;
                      return (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <LocalHospitalIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={hospital} 
                            secondary={`${requestCount} ${requestCount === 1 ? 'Anfrage' : 'Anfragen'} bearbeitet`}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Keine Partnerkliniken vorhanden
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  component={Link}
                  to="/hospitals"
                  color="secondary" 
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Alle Partnerkliniken anzeigen
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </MainLayout>
  );
};

export default Dashboard;
